/**
 * "Why go Pro": the one-time Basic vs Pro comparison sheet.
 *
 * WHY IT EXISTS
 *   Every Pro gate in the app is tease-then-gate at the point of friction, so
 *   a Basic trainer only ever meets Pro one wall at a time and never sees the
 *   whole shape of it. This sheet shows the shape once: two columns, one row
 *   per real gate, ticks and figures rather than prose.
 *
 * WHO SEES IT, AND WHEN
 *   Signed-in Basic trainers only, and only once the tier is SETTLED. `tier`
 *   boots as "free" until entitlements hydrate, so an unsettled read would put
 *   a Pro trainer in front of an upsell for their own subscription. Trial and
 *   Pro never see it. It waits a short delay so it never lands on top of the
 *   first screen, and it yields to any other modal (paywall, legal, onboarding,
 *   questionnaire, guided tour) rather than stacking on it.
 *
 *   Shown once, dismissed forever, same rule as every other prompt in here.
 *   Both exits (Upgrade, Maybe later) mark it seen; the Upgrade path hands off
 *   to the real paywall with its own attribution source.
 */
import { useEffect, useState } from "react";
import { LegalReader } from "@/components/LegalReader";
import { Crown, X } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { usePrices, formatPlanLine } from "@/hooks/usePrices";
import { ProCompareTable } from "@/components/ProCompareTable";
import { useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "@/lib/firstRunPrompts";

export const WHY_PRO_SEEN_KEY = "nx.whyPro.seen.v1";

/** Long enough that the first screen has been seen, short enough to be met. */
export const WHY_PRO_DELAY_MS = 12_000;

/**
 * WHEN IT SHOWS ITSELF (owner decision, 2026-09-11)
 *   Never on first entry: a brand-new trainer gets the app, not a pitch
 *   (2026-09-10 direction stands). The one automatic showing waits for a
 *   RETURN visit: the account is at least a day old, the tier is settled and
 *   Basic, no other modal is up, and the first screen has had its twelve
 *   seconds. Once, then never again. Every feature gate keeps opening the
 *   paywall directly with its own reason; this sheet is the wide view.
 *
 *   The day-old floor, its place in the queue behind the attribution question
 *   and the one-ask-per-session rule all live in lib/firstRunPrompts
 *   (2026-10-23); `useFirstRunPromptDue("why-pro")` is that decision.
 */
export const WHY_PRO_AUTO_SHOW = true;

/**
 * Manual entry points dispatch this on window: the main menu's Basic row
 * ("See what Pro unlocks") and Settings. Opens at once, no delay, seen or not.
 */
export const WHY_PRO_OPEN_EVENT = "nexus:openWhyPro";

export function openWhyPro(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(WHY_PRO_OPEN_EVENT));
}

export function WhyProSheet({ autoShow = WHY_PRO_AUTO_SHOW }: { autoShow?: boolean } = {}): JSX.Element | null {
  const { account } = useAuth();
  const {
    tier, tierSettled, openPaywall,
    paywallOpen, legalOpen, onboardingOpen, nexQuestionnaireOpen, tourOpen, trainerStepOpen,
  } = useSession();
  const { prices } = usePrices();
  const [visible, setVisible] = useState(false);
  /** Opened by hand: shows regardless of the seen flag and the yields. */
  const [manual, setManual] = useState(false);
  /** The Terms / Privacy reader (App Review 3.1.2(c), 2026-10-07). */
  const [legalTab, setLegalTab] = useState<null | "tos" | "privacy">(null);

  const eligible = Boolean(account) && tierSettled && !hasProAccess(tier);
  const due = useFirstRunPromptDue("why-pro");

  useEffect(() => {
    if (!autoShow || !eligible || !due) return;
    const timer = setTimeout(() => {
      claimFirstRunPrompt("why-pro");
      setVisible(true);
    }, WHY_PRO_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoShow, eligible, due]);

  useEffect(() => {
    const open = () => {
      setManual(true);
      setVisible(true);
    };
    window.addEventListener(WHY_PRO_OPEN_EVENT, open);
    return () => window.removeEventListener(WHY_PRO_OPEN_EVENT, open);
  }, []);

  const markSeen = () => {
    setVisible(false);
    setManual(false);
    markFirstRunPromptDone("why-pro");
  };

  const upgrade = () => {
    markSeen();
    openPaywall("why-pro", "Everything Pro unlocks, in one place.");
  };

  const anyModalOpen =
    paywallOpen || legalOpen || onboardingOpen || nexQuestionnaireOpen || tourOpen || trainerStepOpen;
  // A Pro flip after the timer (a purchase elsewhere, a late entitlement) hides
  // it without marking seen: nothing was shown, so there is nothing to remember.
  if (!visible) return null;
  if (!manual && (anyModalOpen || !eligible)) return null;
  if (manual && hasProAccess(tier)) return null;

  const priceLine =
    prices.monthly || prices.annual
      ? `From ${formatPlanLine(prices.monthly, "/mo")} or ${formatPlanLine(prices.annual, "/yr")}`
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Why go Pro"
      data-testid="why-pro-sheet"
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center px-3 py-3 bg-black/75 backdrop-blur-sm anim-fade-up"
      onClick={markSeen}
    >
      {/* The card is the scroller: see PaywallModal (2026-09-11). */}
      <div
        className="relative w-full max-w-md v2-neon-card anim-fade-scale max-h-[90dvh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 30px 80px hsl(0 0% 0% / 0.85), 0 0 0 1px var(--accent-fill-glow), 0 0 40px var(--accent-fill-glow-subtle)",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
        data-testid="why-pro-scroll"
      >
        <button
          type="button"
          onClick={markSeen}
          aria-label="Close"
          data-testid="why-pro-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10"
          style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)" }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-5 pt-6">
          <div className="flex justify-center mb-2.5">
            <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />WHY GO PRO</span>
          </div>
          <h2 className="v2-display text-center text-[22px] leading-tight" style={{ color: "var(--text-primary)" }}>
            Basic is free. Pro is everything.
          </h2>
          <p className="text-center text-[12px] mt-1.5 leading-relaxed px-2" style={{ color: "var(--text-secondary)" }}>
            One look at what changes. No pressure, Basic stays yours.
          </p>

          <ProCompareTable />

          <button
            type="button"
            onClick={upgrade}
            data-testid="why-pro-upgrade"
            className="mt-4 w-full rounded-full py-2.5 text-sm font-bold transition-transform active:scale-95"
            style={{
              background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
              color: "var(--text-on-accent)",
              boxShadow: "0 0 10px var(--accent-fill-glow)",
            }}
          >
            Upgrade to Pro
          </button>
          {priceLine && (
            <p className="mt-1.5 text-center text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="why-pro-price">
              {priceLine}
            </p>
          )}
          <div className="mt-2 flex items-center justify-center gap-2 text-[11px] font-semibold">
            <button
              type="button"
              onClick={() => setLegalTab("tos")}
              className="underline underline-offset-2"
              style={{ color: "var(--text-secondary)" }}
              data-testid="why-pro-terms"
            >
              Terms of Use (EULA)
            </button>
            <span aria-hidden="true" style={{ color: "var(--text-secondary)" }}>·</span>
            <button
              type="button"
              onClick={() => setLegalTab("privacy")}
              className="underline underline-offset-2"
              style={{ color: "var(--text-secondary)" }}
              data-testid="why-pro-privacy"
            >
              Privacy Policy
            </button>
          </div>
          <button
            type="button"
            onClick={markSeen}
            data-testid="why-pro-later"
            className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
      <LegalReader open={legalTab !== null} initialTab={legalTab ?? "tos"} onClose={() => setLegalTab(null)} />
    </div>
  );
}

export default WhyProSheet;
